import { logDebug, logError, logWarn, LogTag } from '../utils/utils';
import { captureCanvasAsJpeg, captureElementWithHtml2Canvas } from '../utils/pdfImageCapture';
import ProjectConfigurationService from './ProjectConfigurationService';
import { EProjectType } from '../enum/project';

/**
 * Interface per i risultati della cattura immagini Container Yard
 */
export interface IContainerYardImages {
    tireImg1: string;
    tireImg2: string;
    tireImg3?: string; // Optional third image for geotextile
    chartImg: string;
}

/**
 * Interface per i risultati della cattura immagini GeoGrid
 */
export interface IGeoGridImages {
    geogridLayersImage: string;
}

/**
 * Interface per i risultati della cattura immagini Unpaved
 */
export interface IUnpavedImages {
    tireImg1: string; // unstabilized or unreinforced
    tireImg2: string; // stabilized (geogrid, geotextile, or reinforced)
    tireImg3?: string; // Optional third image for geotextile
    chartImg: string;
}

/**
 * Interface per i risultati della cattura immagini Paved
 */
export interface IPavedImages {
    tireImageUnreinforced: string; // tire-layer-unstabilized
    tireImageReinforced: string; // tire-layer-increased-passages
    tireImageGeogrid: string; // tire-layer-geogrid
    chartImg: string;
    trafficData?: {
        unstabilized?: any;
        increasedPassages?: any;
        optimized?: any;
    };
    // Dynamic design sections - optional for backward compatibility
    surfaceDesignImages?: {
        canvas1?: string;
        canvas2?: string;
        canvas3?: string;
        tableData1?: {
            passesFirstYear?: number;
            w18?: number;
            thickness?: number;
        };
        tableData2?: {
            passesFirstYear?: number;
            newW18?: number;
            tbr?: number;
        };
        tableData3?: {
            passesFirstYear?: number;
            w18?: number;
            thicknessSaving?: number;
        };
    };
    baseDesignImages?: {
        canvas1?: string;
        canvas2?: string;
        canvas3?: string;
        tableData1?: {
            passesFirstYear?: number;
            w18?: number;
            thickness?: number;
        };
        tableData2?: {
            passesFirstYear?: number;
            newW18?: number;
            tbr?: number;
        };
        tableData3?: {
            passesFirstYear?: number;
            w18?: number;
            thicknessSaving?: number;
        };
    };
    subbaseDesignImages?: {
        canvas1?: string;
        canvas2?: string;
        canvas3?: string;
        tableData1?: {
            passesFirstYear?: number;
            w18?: number;
            thickness?: number;
        };
        tableData2?: {
            passesFirstYear?: number;
            newW18?: number;
            tbr?: number;
        };
        tableData3?: {
            passesFirstYear?: number;
            w18?: number;
            thicknessSaving?: number;
        };
    };
}

/**
 * Interface per i risultati della cattura immagini Airport
 */
export interface IAirportImages {
    tireImageUnstabilized: string; // tire-layer-unstabilized
    tireImageIncreasedPassages: string; // tire-layer-increased-passages
    tireImageGeogrid: string; // tire-layer-geogrid
    chartImg: string; // Grafici PavementLineChartComponent
    // Dynamic design sections - optional for backward compatibility
    surfaceDesignImages?: {
        canvas1?: string;
        canvas2?: string;
        canvas3?: string;
        tableData1?: {
            passesFirstYear?: number;
            w18?: number;
            thickness?: number;
        };
        tableData2?: {
            passesFirstYear?: number;
            newW18?: number;
            tbr?: number;
        };
        tableData3?: {
            passesFirstYear?: number;
            w18?: number;
            thicknessSaving?: number;
        };
    };
    baseDesignImages?: {
        canvas1?: string;
        canvas2?: string;
        canvas3?: string;
        tableData1?: {
            passesFirstYear?: number;
            w18?: number;
            thickness?: number;
        };
        tableData2?: {
            passesFirstYear?: number;
            newW18?: number;
            tbr?: number;
        };
        tableData3?: {
            passesFirstYear?: number;
            w18?: number;
            thicknessSaving?: number;
        };
    };
    subbaseDesignImages?: {
        canvas1?: string;
        canvas2?: string;
        canvas3?: string;
        tableData1?: {
            passesFirstYear?: number;
            w18?: number;
            thickness?: number;
        };
        tableData2?: {
            passesFirstYear?: number;
            newW18?: number;
            tbr?: number;
        };
        tableData3?: {
            passesFirstYear?: number;
            w18?: number;
            thicknessSaving?: number;
        };
    };
}

/**
 * Interface per i risultati della cattura immagini Railway
 */
export interface IRailwayImages {
    tireImageUnreinforced: string; // tire-layer-unreinforced
    tireImageReinforced: string; // tire-layer-reinforced
}

/**
 * Opzioni per la cattura Container Yard
 */
export interface IContainerYardCaptureOptions {
    timeoutMs?: number;
    jpegQuality?: number;
    scale?: number;
    backgroundColor?: string;
}

/**
 * Opzioni per la cattura GeoGrid
 */
export interface IGeoGridCaptureOptions {
    timeoutMs?: number;
    pngQuality?: number;
    scale?: number;
    backgroundColor?: string;
}

/**
 * Opzioni per la cattura Unpaved
 */
export interface IUnpavedCaptureOptions {
    timeoutMs?: number;
    jpegQuality?: number;
    scale?: number;
    backgroundColor?: string;
}

/**
 * Opzioni per la cattura Paved
 */
export interface IPavedCaptureOptions {
    timeoutMs?: number;
    jpegQuality?: number;
    scale?: number;
    backgroundColor?: string;
    trafficData?: {
        unstabilized?: any;
        increasedPassages?: any;
        optimized?: any;
    };
    projectData?: any; // Project data for dynamic canvas discovery
    designTableData?: {
        surfaceDesignTableData?: {
            tableData1?: { passesFirstYear?: number; w18?: number; thickness?: number };
            tableData2?: { passesFirstYear?: number; newW18?: number; tbr?: number };
            tableData3?: { passesFirstYear?: number; w18?: number; thicknessSaving?: number };
        };
        baseDesignTableData?: {
            tableData1?: { passesFirstYear?: number; w18?: number; thickness?: number };
            tableData2?: { passesFirstYear?: number; newW18?: number; tbr?: number };
            tableData3?: { passesFirstYear?: number; w18?: number; thicknessSaving?: number };
        };
        subbaseDesignTableData?: {
            tableData1?: { passesFirstYear?: number; w18?: number; thickness?: number };
            tableData2?: { passesFirstYear?: number; newW18?: number; tbr?: number };
            tableData3?: { passesFirstYear?: number; w18?: number; thicknessSaving?: number };
        };
    };
}

/**
 * Opzioni per la cattura Airport
 */
export interface IAirportCaptureOptions {
    timeoutMs?: number;
    jpegQuality?: number;
    scale?: number;
    backgroundColor?: string;
    projectData?: any; // Project data for dynamic canvas discovery
    designTableData?: any; // Pre-calculated table data for dynamic design sections
}

/**
 * Opzioni per la cattura Railway
 */
export interface IRailwayCaptureOptions {
    timeoutMs?: number;
    jpegQuality?: number;
    scale?: number;
    backgroundColor?: string;
}

/**
 * Service centralizzato per la cattura delle immagini PDF
 * Consolidizza la logica duplicata presente in DesignCondition.tsx, GeogridDesign.tsx, ExportButton.tsx
 */
class PdfImageCaptureService {
    // =====================================
    // DETERMINISTIC SYNCHRONOUS CAPTURE
    // =====================================

    /**
     * Capture Container Yard images using deterministic synchronous approach
     * Avoids timeout issues on remote domains by using direct DOM queries
     * @param options Container Yard capture options
     * @returns Promise<IContainerYardImages>
     */
    public async captureContainerYardImagesDeterministic(options: IContainerYardCaptureOptions = {}): Promise<IContainerYardImages> {
        const { jpegQuality = 0.7 } = options;

        try {
            // Direct synchronous canvas queries - no timeouts needed for elements that already exist
            const canvas1 = this.findCanvasDeterministic(['tire-layer-unstabilized', 'tire-layer-unreinforced']);
            const canvas2 = this.findCanvasDeterministic(['tire-layer-geogrid', 'tire-layer-reinforced']);
            const canvas3 = this.findCanvasDeterministic(['tire-layer-geotextile']); // Optional

            if (!canvas1 || !canvas2) {
                // Throw error to trigger fallback to legacy method
                throw new Error(`Canvas not found: missing ${!canvas1 ? 'primary' : 'secondary'} canvas`);
            }

            // Skip content validation - capture directly if canvases exist (simplified approach)

            const tireImg1 = captureCanvasAsJpeg(canvas1, jpegQuality);
            const tireImg2 = captureCanvasAsJpeg(canvas2, jpegQuality);
            const tireImg3 = canvas3 ? captureCanvasAsJpeg(canvas3, jpegQuality) : '';

            // Simple chart capture
            const chartImg = await this.captureChartDeterministic();

            return {
                tireImg1,
                tireImg2,
                tireImg3,
                chartImg,
            };
        } catch (error) {
            throw error;
        }
    }

    /**
     * Find canvas element deterministically using multiple ID fallbacks
     * @param elementIds Array of element IDs to try in order
     * @returns Canvas element or null
     */
    private findCanvasDeterministic(elementIds: string[]): HTMLCanvasElement | null {
        for (const id of elementIds) {
            const element = document.getElementById(id);

            if (element) {
                const canvas = element.tagName.toLowerCase() === 'canvas' ? (element as HTMLCanvasElement) : (element.querySelector('canvas') as HTMLCanvasElement);

                if (canvas && canvas.width > 0 && canvas.height > 0) {
                    return canvas;
                }
            }
        }
        return null;
    }

    /**
     * Validate canvas content with deterministic retry logic
     * @param canvas Canvas to validate
     * @param elementType Type for logging
     */
    private async validateCanvasContentDeterministic(canvas: HTMLCanvasElement, elementType: string): Promise<void> {
        const maxRetries = 5;
        const retryDelay = 100; // 100ms fixed retry delay

        for (let attempt = 0; attempt < maxRetries; attempt++) {
            try {
                const ctx = canvas.getContext('2d');
                if (ctx) {
                    const imageData = ctx.getImageData(0, 0, Math.min(canvas.width, 100), Math.min(canvas.height, 100));
                    const hasContent = imageData.data.some((value, index) => index % 4 === 3 && value > 0);

                    if (hasContent) {
                        return;
                    }
                }
            } catch (error) {}

            if (attempt < maxRetries - 1) {
                await new Promise(resolve => setTimeout(resolve, retryDelay));
            }
        }
    }

    /**
     * Extract table data from DOM elements below dynamic canvas containers
     * @param containerId The container ID to search for table data
     * @returns Structured table data object or null if not found
     */
    private extractDynamicTableData(containerId: string): { passesFirstYear?: number; w18?: number; newW18?: number; thickness?: number; tbr?: number; thicknessSaving?: number } | null {
        const container = document.getElementById(containerId);
        if (!container) {
            return null;
        }

        logDebug(
            'PdfImageCaptureService',
            `Starting table extraction for container: ${containerId}`,
            {
                containerFound: !!container,
                containerTagName: container.tagName,
                containerClassName: container.className,
                containerChildren: container.children.length,
            },
            LogTag.PDF_IMAGES
        );

        // Log all siblings to understand DOM structure
        let currentElement = container.nextElementSibling;
        const siblingInfo: { tagName: string; className: string; hasTableContent: boolean; index: number }[] = [];
        let siblingIndex = 0;

        while (currentElement && siblingIndex < 5) {
            // Limit to first 5 siblings for logging
            const hasFlexElements = currentElement.querySelectorAll('div, [class*="MuiBox"], [class*="css-"]').length > 0;
            const hasTypographyElements = currentElement.querySelectorAll('p, [class*="MuiTypography"], span').length > 0;
            const textContent = currentElement.textContent?.toLowerCase() || '';
            const hasTableKeywords = textContent.includes('passes') || textContent.includes('w18') || textContent.includes('tbr') || textContent.includes('thickness');

            siblingInfo.push({
                tagName: currentElement.tagName,
                className: currentElement.className,
                hasTableContent: hasFlexElements && hasTypographyElements && hasTableKeywords,
                index: siblingIndex,
            });

            currentElement = currentElement.nextElementSibling;
            siblingIndex++;
        }

        // Strategy 1: Look for Material-UI Box components with table content
        currentElement = container.nextElementSibling;
        let tableBox: Element | null = null;

        while (currentElement) {
            // Look for Material-UI Box elements or divs with table-like content
            const isBox = currentElement.tagName === 'DIV' && (currentElement.className.includes('MuiBox') || currentElement.className.includes('css-'));

            if (isBox) {
                // Check if this box contains table data by looking for typography elements with keywords
                const textContent = currentElement.textContent?.toLowerCase() || '';
                const hasTableKeywords = textContent.includes('passes') || textContent.includes('w18') || textContent.includes('tbr') || textContent.includes('thickness');

                if (hasTableKeywords) {
                    tableBox = currentElement;
                    logDebug(
                        'PdfImageCaptureService',
                        `Found table box via Material-UI strategy for ${containerId}`,
                        {
                            tagName: currentElement.tagName,
                            className: currentElement.className,
                            textPreview: textContent.substring(0, 100),
                        },
                        LogTag.PDF_IMAGES
                    );
                    break;
                }
            }
            currentElement = currentElement.nextElementSibling;
        }

        // Strategy 2: Fallback to any element with table keywords
        if (!tableBox) {
            currentElement = container.nextElementSibling;
            while (currentElement) {
                const textContent = currentElement.textContent?.toLowerCase() || '';
                const hasTableKeywords = textContent.includes('passes') || textContent.includes('w18') || textContent.includes('tbr') || textContent.includes('thickness');

                if (hasTableKeywords) {
                    tableBox = currentElement;
                    break;
                }
                currentElement = currentElement.nextElementSibling;
            }
        }

        if (!tableBox) {
            return null;
        }

        const tableData: { passesFirstYear?: number; w18?: number; newW18?: number; thickness?: number; tbr?: number; thicknessSaving?: number } = {};

        // Strategy 1: Look for Material-UI Box components with flex layout
        let rows = tableBox.querySelectorAll('div[class*="MuiBox"], div[class*="css-"]');

        // Filter to only include rows that actually contain Typography elements
        const filteredMuiRows: Element[] = [];
        for (const row of Array.from(rows)) {
            const hasTypography = row.querySelectorAll('p, span, [class*="MuiTypography"]').length > 0;
            if (hasTypography) {
                filteredMuiRows.push(row);
            }
        }
        rows = filteredMuiRows as any;

        logDebug(
            'PdfImageCaptureService',
            `Found ${rows.length} Material-UI rows via MUI selector for ${containerId}`,
            {
                rowsFound: rows.length,
            },
            LogTag.PDF_IMAGES
        );

        // Strategy 2: Fallback to broader div selection with Typography content
        if (rows.length === 0) {
            rows = tableBox.querySelectorAll('div');
            const divsWithParagraphs: Element[] = [];
            for (const row of Array.from(rows)) {
                const hasParagraphs = row.querySelectorAll('p').length > 0;
                if (hasParagraphs) {
                    divsWithParagraphs.push(row);
                }
            }
            rows = divsWithParagraphs as any;
        }

        // Strategy 3: Final fallback - all child divs that contain text
        if (rows.length === 0) {
            rows = tableBox.querySelectorAll('div');
            const filteredRows: Element[] = [];
            for (const row of Array.from(rows)) {
                const hasText = row.textContent && row.textContent.trim().length > 0;
                const hasRelevantText = row.textContent?.toLowerCase().match(/(passes|w18|tbr|thickness)/);
                if (hasText && hasRelevantText) {
                    filteredRows.push(row);
                }
            }
            rows = filteredRows as any; // Cast to NodeList-like
        }

        for (const row of Array.from(rows)) {
            // Look for Typography elements (p, span) within the row
            const typographyElements = row.querySelectorAll('p, span, [class*="MuiTypography"]');

            if (typographyElements.length >= 2) {
                // Assume first element is label, last element is value
                const labelElement = typographyElements[0];
                const valueElement = typographyElements[typographyElements.length - 1];

                const labelText = labelElement.textContent?.toLowerCase() || '';
                const valueText = valueElement.textContent || '';

                logDebug(
                    'PdfImageCaptureService',
                    `Processing table row for ${containerId}`,
                    {
                        label: labelText,
                        value: valueText,
                        elementsFound: typographyElements.length,
                    },
                    LogTag.PDF_IMAGES
                );

                // Parse numeric value (remove commas and non-numeric characters except decimal point and percentage)
                const cleanValue = valueText.replace(/[,%]/g, '').replace(/[^\d.-]/g, '');
                const numericValue = parseFloat(cleanValue);

                if (!isNaN(numericValue)) {
                    // Map labels to data fields with more flexible matching
                    if (labelText.includes('passes') && (labelText.includes('1st year') || labelText.includes('first year'))) {
                        tableData.passesFirstYear = numericValue;
                    } else if (labelText.includes('new') && labelText.includes('w18')) {
                        tableData.newW18 = numericValue;
                    } else if (labelText.includes('w18') && !labelText.includes('new')) {
                        tableData.w18 = numericValue;
                    } else if (labelText.includes('thickness') && !labelText.includes('saving')) {
                        tableData.thickness = numericValue;
                    } else if (labelText.includes('thickness') && labelText.includes('saving')) {
                        tableData.thicknessSaving = numericValue;
                    } else if (labelText.includes('tbr')) {
                        tableData.tbr = numericValue;
                    }
                } else {
                }
            }
        }

        return Object.keys(tableData).length > 0 ? tableData : null;
    }

    /**
     * TEMPORARY: Debug method for testing table extraction in browser console
     * Usage: window.debugTableExtraction('surface-design-unstabilized')
     */
    public static debugTableExtraction(containerId: string): void {
        const service = new PdfImageCaptureService();
        const result = service.extractDynamicTableData(containerId);
        console.log(`🔍 Table extraction debug for: ${containerId}`);
        console.log(`📊 Result:`, result);

        // Also log the DOM structure for inspection
        const container = document.getElementById(containerId);
        if (container) {
            console.log(`📍 Container:`, container);
            console.log(`📍 Container next sibling:`, container.nextElementSibling);
            const siblings = container.parentElement ? Array.from(container.parentElement.children) : [];
            const containerIndex = siblings.indexOf(container);
            const nextSiblings = siblings.slice(containerIndex + 1, containerIndex + 4);
            console.log(`📍 All siblings:`, nextSiblings);
        }

        // Also test all surface design containers
        if (containerId === 'test-all') {
            const containers = ['surface-design-unstabilized', 'surface-design-increased-passages', 'surface-design-stabilized'];
            containers.forEach(id => {
                console.log(`\n=== Testing ${id} ===`);
                service.extractDynamicTableData(id);
            });
        }
    }

    // Make debug method available in browser console
    static {
        if (typeof window !== 'undefined') {
            (window as any).debugTableExtraction = PdfImageCaptureService.debugTableExtraction;
        }
    }

    /**
     * Capture chart element deterministically
     * @returns Chart image as base64 string or empty string
     */
    private async captureChartDeterministic(): Promise<string> {
        const chartElement = document.getElementById('line-chart-results');
        if (chartElement) {
            try {
                const rect = chartElement.getBoundingClientRect();
                if (rect.width > 0 && rect.height > 0) {
                    return await captureElementWithHtml2Canvas(chartElement, {
                        scale: 1,
                        backgroundColor: '#FFFFFF',
                        useCORS: true,
                        allowTaint: false,
                    });
                }
            } catch (error) {}
        }
        return '';
    }

    // =====================================
    // SIMPLIFIED DETERMINISTIC METHODS
    // =====================================

    /**
     * Cattura le immagini per Container Yard usando approccio deterministico
     * @param options opzioni di cattura
     * @returns Promise<IContainerYardImages>
     */
    public async captureContainerYardImages(options: IContainerYardCaptureOptions = {}): Promise<IContainerYardImages> {
        const { jpegQuality = 0.7 } = options;

        try {
            // Direct synchronous canvas queries - no timeouts needed for elements that already exist
            const canvas1 = this.findCanvasDeterministic(['tire-layer-unstabilized', 'tire-layer-unreinforced']);
            const canvas2 = this.findCanvasDeterministic(['tire-layer-geogrid', 'tire-layer-reinforced']);
            const canvas3 = this.findCanvasDeterministic(['tire-layer-geotextile']); // Optional

            if (!canvas1 || !canvas2) {
                // Throw error to trigger fallback to legacy method
                throw new Error(`Canvas not found: missing ${!canvas1 ? 'primary' : 'secondary'} canvas`);
            }

            // Skip content validation - capture directly if canvases exist (simplified approach)

            const tireImg1 = captureCanvasAsJpeg(canvas1, jpegQuality);
            const tireImg2 = captureCanvasAsJpeg(canvas2, jpegQuality);
            const tireImg3 = canvas3 ? captureCanvasAsJpeg(canvas3, jpegQuality) : '';

            // Simple chart capture
            const chartImg = await this.captureChartDeterministic();

            return {
                tireImg1,
                tireImg2,
                tireImg3,
                chartImg,
            };
        } catch (error) {
            throw error;
        }
    }

    /**
     * Cattura le immagini per GeoGrid (canvas container)
     * @param options opzioni di cattura
     * @returns Promise<IGeoGridImages>
     */
    public async captureGeoGridImages(options: IGeoGridCaptureOptions = {}): Promise<IGeoGridImages> {
        const { pngQuality = 0.9, scale = 1, backgroundColor = '#FFFFFF' } = options;

        try {
            // Query diretta DOM senza timeout per canvas-container
            const geogridElement = document.getElementById('canvas-container');

            if (!geogridElement) {
                throw new Error('GeoGrid canvas-container not found with deterministic approach');
            }

            logDebug(
                'PdfImageCaptureService',
                'Found GeoGrid canvas-container deterministically:',
                {
                    elementId: geogridElement.id,
                },
                LogTag.PDF_IMAGES
            );

            // Wait for images to be loaded - check data-images-loaded attribute
            const maxImageWaitRetries = 10;
            const imageWaitDelay = 200; // 200ms between checks
            let imagesLoaded = false;

            for (let i = 0; i < maxImageWaitRetries; i++) {
                const loadedAttribute = geogridElement.getAttribute('data-images-loaded');
                if (loadedAttribute === 'true') {
                    imagesLoaded = true;
                    break;
                }

                if (i < maxImageWaitRetries - 1) {
                    await new Promise(resolve => setTimeout(resolve, imageWaitDelay));
                }
            }

            if (!imagesLoaded) {
            }

            // Ottieni le dimensioni reali del container
            const rect = geogridElement.getBoundingClientRect();

            if (rect.width <= 0 || rect.height <= 0) {
                // Retry deterministico con delay fisso se dimensioni non valide
                const maxRetries = 5;
                const retryDelay = 100;

                for (let i = 0; i < maxRetries; i++) {
                    await new Promise(resolve => setTimeout(resolve, retryDelay));
                    const newRect = geogridElement.getBoundingClientRect();

                    if (newRect.width > 0 && newRect.height > 0) {
                        logDebug(
                            'PdfImageCaptureService',
                            `GeoGrid container dimensions validated after ${i + 1} retries:`,
                            {
                                width: newRect.width,
                                height: newRect.height,
                            },
                            LogTag.PDF_IMAGES
                        );
                        break;
                    }

                    if (i === maxRetries - 1) {
                        throw new Error(`GeoGrid container has invalid dimensions after ${maxRetries} retries`);
                    }
                }
            }

            // Cattura l'elemento GeoGrid
            const geogridLayersImage = await captureElementWithHtml2Canvas(geogridElement, {
                scale,
                backgroundColor,
                width: rect.width,
                height: rect.height,
                useCORS: true,
                allowTaint: true,
                quality: pngQuality,
                format: 'png',
            });

            logDebug(
                'PdfImageCaptureService',
                'GeoGrid image captured deterministically:',
                {
                    geogridImageLength: geogridLayersImage.length,
                },
                LogTag.PDF_IMAGES
            );

            return {
                geogridLayersImage,
            };
        } catch (error) {
            throw error;
        }
    }

    /**
     * Cattura le immagini per Unpaved usando approccio deterministico
     * @param options opzioni di cattura
     * @returns Promise<IUnpavedImages>
     */
    public async captureUnpavedImages(options: IUnpavedCaptureOptions = {}): Promise<IUnpavedImages> {
        const { jpegQuality = 0.7 } = options;

        try {
            // Direct canvas queries for unpaved elements
            const canvas1 = this.findCanvasDeterministic(['tire-layer-unstabilized', 'tire-layer-unreinforced']);
            const canvas2 = this.findCanvasDeterministic(['tire-layer-geogrid', 'tire-layer-reinforced']);
            const canvas3 = this.findCanvasDeterministic(['tire-layer-geotextile']); // Optional

            logDebug(
                'PdfImageCaptureService',
                'Unpaved canvas discovery results:',
                {
                    canvas1Found: !!canvas1,
                    canvas1Id: canvas1 ? canvas1.id || 'no-id' : 'not-found',
                    canvas1Dimensions: canvas1 ? `${canvas1.width}x${canvas1.height}` : 'N/A',
                    canvas2Found: !!canvas2,
                    canvas2Id: canvas2 ? canvas2.id || 'no-id' : 'not-found',
                    canvas2Dimensions: canvas2 ? `${canvas2.width}x${canvas2.height}` : 'N/A',
                    canvas3Found: !!canvas3,
                    canvas3Id: canvas3 ? canvas3.id || 'no-id' : 'not-found',
                    canvas3Dimensions: canvas3 ? `${canvas3.width}x${canvas3.height}` : 'N/A',
                },
                LogTag.PDF_IMAGES
            );

            if (!canvas1 || !canvas2) {
                throw new Error(`Unpaved canvas not found: missing ${!canvas1 ? 'primary' : 'secondary'} canvas`);
            }

            // Validate canvas content
            await this.validateCanvasContentDeterministic(canvas1, 'primary');
            await this.validateCanvasContentDeterministic(canvas2, 'secondary');

            // For canvas3 (geotextile), validate if it exists
            if (canvas3) {
                await this.validateCanvasContentDeterministic(canvas3, 'geotextile');
            }

            const tireImg1 = captureCanvasAsJpeg(canvas1, jpegQuality);
            const tireImg2 = captureCanvasAsJpeg(canvas2, jpegQuality);

            // Enhanced capture logic for tireImg3 with error handling
            let tireImg3 = '';
            if (canvas3) {
                try {
                    logDebug(
                        'PdfImageCaptureService',
                        'Attempting to capture geotextile canvas...',
                        {
                            canvasWidth: canvas3.width,
                            canvasHeight: canvas3.height,
                            canvasId: canvas3.id,
                        },
                        LogTag.PDF_IMAGES
                    );

                    tireImg3 = captureCanvasAsJpeg(canvas3, jpegQuality);

                    if (!tireImg3 || tireImg3.length === 0) {
                        // Still set to empty string to indicate geotextile was expected but failed
                        tireImg3 = '';
                    }
                } catch (error) {
                    tireImg3 = '';
                }
            }

            logDebug(
                'PdfImageCaptureService',
                'Unpaved image capture results:',
                {
                    tireImg1Length: tireImg1.length,
                    tireImg1StartsWith: tireImg1.startsWith('data:image/'),
                    tireImg2Length: tireImg2.length,
                    tireImg2StartsWith: tireImg2.startsWith('data:image/'),
                    tireImg3Length: tireImg3.length,
                    tireImg3StartsWith: tireImg3.length > 0 ? tireImg3.startsWith('data:image/') : false,
                    tireImg3Value: tireImg3 === '' ? 'EMPTY_STRING' : 'HAS_DATA',
                    canvas3Found: !!canvas3,
                },
                LogTag.PDF_IMAGES
            );

            const chartImg = await this.captureChartDeterministic();

            return {
                tireImg1,
                tireImg2,
                tireImg3,
                chartImg,
            };
        } catch (error) {
            throw error;
        }
    }

    /**
     * Cattura le immagini per Paved usando approccio deterministico
     * @param options opzioni di cattura
     * @returns Promise<IPavedImages>
     */
    public async capturePavedImages(options: IPavedCaptureOptions = {}): Promise<IPavedImages> {
        const { jpegQuality = 0.7, trafficData, projectData, designTableData } = options;

        try {
            // Direct canvas queries for paved elements (3 required canvas)
            const canvasUnreinforced = this.findCanvasDeterministic(['tire-layer-unstabilized']);
            const canvasReinforced = this.findCanvasDeterministic(['tire-layer-increased-passages']);
            const canvasGeogrid = this.findCanvasDeterministic(['tire-layer-geogrid']);

            if (!canvasUnreinforced || !canvasReinforced || !canvasGeogrid) {
                const missing = [];
                if (!canvasUnreinforced) missing.push('unstabilized');
                if (!canvasReinforced) missing.push('increased-passages');
                if (!canvasGeogrid) missing.push('geogrid');
                throw new Error(`Paved canvas not found: missing ${missing.join(', ')} canvas`);
            }

            // Validate canvas content
            await this.validateCanvasContentDeterministic(canvasUnreinforced, 'unstabilized');
            await this.validateCanvasContentDeterministic(canvasReinforced, 'increased-passages');
            await this.validateCanvasContentDeterministic(canvasGeogrid, 'geogrid');

            const tireImageUnreinforced = captureCanvasAsJpeg(canvasUnreinforced, jpegQuality);
            const tireImageReinforced = captureCanvasAsJpeg(canvasReinforced, jpegQuality);
            const tireImageGeogrid = captureCanvasAsJpeg(canvasGeogrid, jpegQuality);

            // Capture chart if present
            const chartImg = await this.captureChartDeterministic();

            // Capture dynamic design canvas elements using data-driven approach
            let surfaceDesignImages: IPavedImages['surfaceDesignImages'] = undefined;
            let baseDesignImages: IPavedImages['baseDesignImages'] = undefined;
            let subbaseDesignImages: IPavedImages['subbaseDesignImages'] = undefined;

            // 🔧 FIX: Capture canvas even without designTableData (tables will be empty but canvas can still be captured)
            if (projectData) {
                logDebug(
                    'PdfImageCaptureService',
                    'Using data-driven approach for dynamic design canvas elements...',
                    {
                        surfaceEnabled: projectData.surfaceDesign?.enableSurfaceDesign,
                        baseEnabled: projectData.baseDesign?.enableBaseDesign,
                        subbaseEnabled: projectData.subbaseDesign?.enableSubbaseDesign,
                        hasDesignTableData: !!designTableData,
                    },
                    LogTag.DYNAMIC_CANVAS
                );

                // Get dynamic canvas IDs based on enabled toggles
                const dynamicCanvasIds = ProjectConfigurationService.getDynamicPavedCanvasIds(projectData);

                if (dynamicCanvasIds.length > 0) {
                    logDebug('PdfImageCaptureService', `Found ${dynamicCanvasIds.length} dynamic canvas elements to capture`, { dynamicCanvasIds }, LogTag.DYNAMIC_CANVAS);

                    // Capture surface design canvas elements (with optional table data)
                    // 🔧 FIX: Capture canvas even when designTableData is undefined (table data will be empty)
                    if (projectData.surfaceDesign?.enableSurfaceDesign === 'yes') {
                        logDebug('PdfImageCaptureService', 'Capturing surface design canvases...', { hasDesignTableData: !!designTableData?.surfaceDesignTableData }, LogTag.DYNAMIC_CANVAS);

                        surfaceDesignImages = {};

                        // Find canvas elements by container ID
                        const surfaceCanvas1 = document.getElementById('surface-design-unstabilized')?.querySelector('canvas');
                        const surfaceCanvas2 = document.getElementById('surface-design-increased-passages')?.querySelector('canvas');
                        const surfaceCanvas3 = document.getElementById('surface-design-stabilized')?.querySelector('canvas');

                        logDebug(
                            'PdfImageCaptureService',
                            'Surface canvas elements found by container ID',
                            {
                                canvas1Found: !!surfaceCanvas1,
                                canvas2Found: !!surfaceCanvas2,
                                canvas3Found: !!surfaceCanvas3,
                                canvas1Dimensions: surfaceCanvas1 ? `${surfaceCanvas1.width}x${surfaceCanvas1.height}` : 'N/A',
                                canvas2Dimensions: surfaceCanvas2 ? `${surfaceCanvas2.width}x${surfaceCanvas2.height}` : 'N/A',
                                canvas3Dimensions: surfaceCanvas3 ? `${surfaceCanvas3.width}x${surfaceCanvas3.height}` : 'N/A',
                            },
                            LogTag.DYNAMIC_CANVAS
                        );

                        // Capture canvas images and include pre-calculated table data if available
                        if (surfaceCanvas1) {
                            surfaceDesignImages.canvas1 = captureCanvasAsJpeg(surfaceCanvas1, jpegQuality);
                            if (designTableData?.surfaceDesignTableData?.tableData1) {
                                surfaceDesignImages.tableData1 = designTableData.surfaceDesignTableData.tableData1;
                            }
                        }
                        if (surfaceCanvas2) {
                            surfaceDesignImages.canvas2 = captureCanvasAsJpeg(surfaceCanvas2, jpegQuality);
                            if (designTableData?.surfaceDesignTableData?.tableData2) {
                                surfaceDesignImages.tableData2 = designTableData.surfaceDesignTableData.tableData2;
                            }
                        }
                        if (surfaceCanvas3) {
                            surfaceDesignImages.canvas3 = captureCanvasAsJpeg(surfaceCanvas3, jpegQuality);
                            if (designTableData?.surfaceDesignTableData?.tableData3) {
                                surfaceDesignImages.tableData3 = designTableData.surfaceDesignTableData.tableData3;
                            }
                        }

                        logDebug(
                            'PdfImageCaptureService',
                            'Surface design images and table data captured',
                            {
                                hasCanvas1: !!surfaceDesignImages.canvas1,
                                hasCanvas2: !!surfaceDesignImages.canvas2,
                                hasCanvas3: !!surfaceDesignImages.canvas3,
                                canvas1Length: surfaceDesignImages.canvas1?.length || 0,
                                canvas2Length: surfaceDesignImages.canvas2?.length || 0,
                                canvas3Length: surfaceDesignImages.canvas3?.length || 0,
                                hasTableData1: !!surfaceDesignImages.tableData1,
                                hasTableData2: !!surfaceDesignImages.tableData2,
                                hasTableData3: !!surfaceDesignImages.tableData3,
                                tableData1: surfaceDesignImages.tableData1,
                                tableData2: surfaceDesignImages.tableData2,
                                tableData3: surfaceDesignImages.tableData3,
                            },
                            LogTag.DYNAMIC_CANVAS
                        );
                    }

                    // Capture base design canvas elements (with optional table data)
                    // 🔧 FIX: Capture canvas even when designTableData is undefined (table data will be empty)
                    if (projectData.baseDesign?.enableBaseDesign === 'yes') {
                        logDebug('PdfImageCaptureService', 'Capturing base design canvases...', { hasDesignTableData: !!designTableData?.baseDesignTableData }, LogTag.DYNAMIC_CANVAS);

                        baseDesignImages = {};

                        // Find canvas elements by container ID
                        const baseCanvas1 = document.getElementById('base-design-unstabilized')?.querySelector('canvas');
                        const baseCanvas2 = document.getElementById('base-design-increased-passages')?.querySelector('canvas');
                        const baseCanvas3 = document.getElementById('base-design-stabilized')?.querySelector('canvas');

                        logDebug(
                            'PdfImageCaptureService',
                            'Base canvas elements found by container ID',
                            {
                                canvas1Found: !!baseCanvas1,
                                canvas2Found: !!baseCanvas2,
                                canvas3Found: !!baseCanvas3,
                                canvas1Dimensions: baseCanvas1 ? `${baseCanvas1.width}x${baseCanvas1.height}` : 'N/A',
                                canvas2Dimensions: baseCanvas2 ? `${baseCanvas2.width}x${baseCanvas2.height}` : 'N/A',
                                canvas3Dimensions: baseCanvas3 ? `${baseCanvas3.width}x${baseCanvas3.height}` : 'N/A',
                            },
                            LogTag.DYNAMIC_CANVAS
                        );

                        // Capture canvas images and include pre-calculated table data if available
                        if (baseCanvas1) {
                            baseDesignImages.canvas1 = captureCanvasAsJpeg(baseCanvas1, jpegQuality);
                            if (designTableData?.baseDesignTableData?.tableData1) {
                                baseDesignImages.tableData1 = designTableData.baseDesignTableData.tableData1;
                            }
                        }
                        if (baseCanvas2) {
                            baseDesignImages.canvas2 = captureCanvasAsJpeg(baseCanvas2, jpegQuality);
                            if (designTableData?.baseDesignTableData?.tableData2) {
                                baseDesignImages.tableData2 = designTableData.baseDesignTableData.tableData2;
                            }
                        }
                        if (baseCanvas3) {
                            baseDesignImages.canvas3 = captureCanvasAsJpeg(baseCanvas3, jpegQuality);
                            if (designTableData?.baseDesignTableData?.tableData3) {
                                baseDesignImages.tableData3 = designTableData.baseDesignTableData.tableData3;
                            }
                        }

                        logDebug(
                            'PdfImageCaptureService',
                            'Base design images and table data captured',
                            {
                                hasCanvas1: !!baseDesignImages.canvas1,
                                hasCanvas2: !!baseDesignImages.canvas2,
                                hasCanvas3: !!baseDesignImages.canvas3,
                                canvas1Length: baseDesignImages.canvas1?.length || 0,
                                canvas2Length: baseDesignImages.canvas2?.length || 0,
                                canvas3Length: baseDesignImages.canvas3?.length || 0,
                                hasTableData1: !!baseDesignImages.tableData1,
                                hasTableData2: !!baseDesignImages.tableData2,
                                hasTableData3: !!baseDesignImages.tableData3,
                                tableData1: baseDesignImages.tableData1,
                                tableData2: baseDesignImages.tableData2,
                                tableData3: baseDesignImages.tableData3,
                            },
                            LogTag.DYNAMIC_CANVAS
                        );
                    }

                    // Capture subbase design canvas elements (with optional table data)
                    // 🔧 FIX: Capture canvas even when designTableData is undefined (table data will be empty)
                    if (projectData.subbaseDesign?.enableSubbaseDesign === 'yes') {
                        logDebug('PdfImageCaptureService', 'Capturing subbase design canvases...', { hasDesignTableData: !!designTableData?.subbaseDesignTableData }, LogTag.DYNAMIC_CANVAS);

                        subbaseDesignImages = {};

                        // Find canvas elements by container ID
                        const subbaseCanvas1 = document.getElementById('subbase-design-unstabilized')?.querySelector('canvas');
                        const subbaseCanvas2 = document.getElementById('subbase-design-increased-passages')?.querySelector('canvas');
                        const subbaseCanvas3 = document.getElementById('subbase-design-stabilized')?.querySelector('canvas');

                        logDebug(
                            'PdfImageCaptureService',
                            'Subbase canvas elements found by container ID',
                            {
                                canvas1Found: !!subbaseCanvas1,
                                canvas2Found: !!subbaseCanvas2,
                                canvas3Found: !!subbaseCanvas3,
                                canvas1Dimensions: subbaseCanvas1 ? `${subbaseCanvas1.width}x${subbaseCanvas1.height}` : 'N/A',
                                canvas2Dimensions: subbaseCanvas2 ? `${subbaseCanvas2.width}x${subbaseCanvas2.height}` : 'N/A',
                                canvas3Dimensions: subbaseCanvas3 ? `${subbaseCanvas3.width}x${subbaseCanvas3.height}` : 'N/A',
                            },
                            LogTag.DYNAMIC_CANVAS
                        );

                        // Capture canvas images and include pre-calculated table data if available
                        if (subbaseCanvas1) {
                            subbaseDesignImages.canvas1 = captureCanvasAsJpeg(subbaseCanvas1, jpegQuality);
                            if (designTableData?.subbaseDesignTableData?.tableData1) {
                                subbaseDesignImages.tableData1 = designTableData.subbaseDesignTableData.tableData1;
                            }
                        }
                        if (subbaseCanvas2) {
                            subbaseDesignImages.canvas2 = captureCanvasAsJpeg(subbaseCanvas2, jpegQuality);
                            if (designTableData?.subbaseDesignTableData?.tableData2) {
                                subbaseDesignImages.tableData2 = designTableData.subbaseDesignTableData.tableData2;
                            }
                        }
                        if (subbaseCanvas3) {
                            subbaseDesignImages.canvas3 = captureCanvasAsJpeg(subbaseCanvas3, jpegQuality);
                            if (designTableData?.subbaseDesignTableData?.tableData3) {
                                subbaseDesignImages.tableData3 = designTableData.subbaseDesignTableData.tableData3;
                            }
                        }

                        logDebug(
                            'PdfImageCaptureService',
                            'Subbase design images and table data captured',
                            {
                                hasCanvas1: !!subbaseDesignImages.canvas1,
                                hasCanvas2: !!subbaseDesignImages.canvas2,
                                hasCanvas3: !!subbaseDesignImages.canvas3,
                                canvas1Length: subbaseDesignImages.canvas1?.length || 0,
                                canvas2Length: subbaseDesignImages.canvas2?.length || 0,
                                canvas3Length: subbaseDesignImages.canvas3?.length || 0,
                                hasTableData1: !!subbaseDesignImages.tableData1,
                                hasTableData2: !!subbaseDesignImages.tableData2,
                                hasTableData3: !!subbaseDesignImages.tableData3,
                                tableData1: subbaseDesignImages.tableData1,
                                tableData2: subbaseDesignImages.tableData2,
                                tableData3: subbaseDesignImages.tableData3,
                            },
                            LogTag.DYNAMIC_CANVAS
                        );
                    }
                }
            }
            // Note: Canvas capture now works even without designTableData (table data will be empty but canvas will be captured)

            return {
                tireImageUnreinforced,
                tireImageReinforced,
                tireImageGeogrid,
                chartImg,
                trafficData,
                surfaceDesignImages,
                baseDesignImages,
                subbaseDesignImages,
            };
        } catch (error) {
            throw error;
        }
    }

    /**
     * Cattura le immagini per Airport usando approccio deterministico
     * @param options opzioni di cattura
     * @returns Promise<IAirportImages>
     */
    public async captureAirportImages(options: IAirportCaptureOptions = {}): Promise<IAirportImages> {
        const { jpegQuality = 0.8, projectData, designTableData } = options;

        try {
            // Direct canvas queries for airport elements (same as paved)
            const canvasUnstabilized = this.findCanvasDeterministic(['tire-layer-unstabilized']);
            const canvasIncreasedPassages = this.findCanvasDeterministic(['tire-layer-increased-passages']);
            const canvasGeogrid = this.findCanvasDeterministic(['tire-layer-geogrid']);

            if (!canvasUnstabilized || !canvasIncreasedPassages || !canvasGeogrid) {
                const missing = [];
                if (!canvasUnstabilized) missing.push('unstabilized');
                if (!canvasIncreasedPassages) missing.push('increased-passages');
                if (!canvasGeogrid) missing.push('geogrid');
                throw new Error(`Airport canvas not found: missing ${missing.join(', ')} canvas`);
            }

            // Validate canvas content
            await this.validateCanvasContentDeterministic(canvasUnstabilized, 'unstabilized');
            await this.validateCanvasContentDeterministic(canvasIncreasedPassages, 'increased-passages');
            await this.validateCanvasContentDeterministic(canvasGeogrid, 'geogrid');

            const tireImageUnstabilized = captureCanvasAsJpeg(canvasUnstabilized, jpegQuality);
            const tireImageIncreasedPassages = captureCanvasAsJpeg(canvasIncreasedPassages, jpegQuality);
            const tireImageGeogrid = captureCanvasAsJpeg(canvasGeogrid, jpegQuality);

            // Airport projects don't typically use charts
            const chartImg = '';

            // Capture dynamic design canvas elements (same pattern as Paved)
            let surfaceDesignImages: IAirportImages['surfaceDesignImages'] = undefined;
            let baseDesignImages: IAirportImages['baseDesignImages'] = undefined;
            let subbaseDesignImages: IAirportImages['subbaseDesignImages'] = undefined;

            console.log('[AIRPORT_DEBUG] Checking dynamic canvas prerequisites:', {
                hasProjectData: !!projectData,
                hasDesignTableData: !!designTableData,
                surfaceDesignEnabled: projectData?.surfaceDesign?.enableSurfaceDesign,
                baseDesignEnabled: projectData?.baseDesign?.enableBaseDesign,
                subbaseDesignEnabled: projectData?.subbaseDesign?.enableSubbaseDesign,
            });

            if (projectData && designTableData) {
                logDebug(
                    'PdfImageCaptureService',
                    'Using data-driven approach for Airport dynamic design canvas elements...',
                    {
                        surfaceEnabled: projectData.surfaceDesign?.enableSurfaceDesign,
                        baseEnabled: projectData.baseDesign?.enableBaseDesign,
                        subbaseEnabled: projectData.subbaseDesign?.enableSubbaseDesign,
                        hasDesignTableData: !!designTableData,
                    },
                    LogTag.DYNAMIC_CANVAS
                );

                // Capture surface design canvas elements with pre-calculated table data
                console.log('[AIRPORT_DEBUG] Checking surface design requirements:', {
                    surfaceEnabled: projectData.surfaceDesign?.enableSurfaceDesign,
                    hasTableData: !!designTableData.surfaceDesignTableData,
                    willCaptureSurface: projectData.surfaceDesign?.enableSurfaceDesign === 'yes' && !!designTableData.surfaceDesignTableData,
                });

                if (projectData.surfaceDesign?.enableSurfaceDesign === 'yes' && designTableData.surfaceDesignTableData) {
                    console.log('[AIRPORT_DEBUG] ===== ENTERING SURFACE DESIGN CAPTURE =====');
                    logDebug('PdfImageCaptureService', 'Capturing Airport surface design canvases with pre-calculated table data...', undefined, LogTag.DYNAMIC_CANVAS);

                    surfaceDesignImages = {};

                    // Find canvas elements by container ID (same IDs as Paved/Airport use same structure)
                    console.log('[AIRPORT_DEBUG] Searching for surface canvas elements...');
                    const surfaceCanvas1 = document.getElementById('surface-design-unstabilized')?.querySelector('canvas');
                    const surfaceCanvas2 = document.getElementById('surface-design-increased-passages')?.querySelector('canvas');
                    const surfaceCanvas3 = document.getElementById('surface-design-stabilized')?.querySelector('canvas');

                    console.log('[AIRPORT_DEBUG] Surface canvas search results:', {
                        unstabilized: { element: !!document.getElementById('surface-design-unstabilized'), canvas: !!surfaceCanvas1 },
                        increasedPassages: { element: !!document.getElementById('surface-design-increased-passages'), canvas: !!surfaceCanvas2 },
                        stabilized: { element: !!document.getElementById('surface-design-stabilized'), canvas: !!surfaceCanvas3 },
                    });

                    logDebug(
                        'PdfImageCaptureService',
                        'Airport surface canvas elements found by container ID',
                        {
                            canvas1Found: !!surfaceCanvas1,
                            canvas2Found: !!surfaceCanvas2,
                            canvas3Found: !!surfaceCanvas3,
                            canvas1Dimensions: surfaceCanvas1 ? `${surfaceCanvas1.width}x${surfaceCanvas1.height}` : 'N/A',
                            canvas2Dimensions: surfaceCanvas2 ? `${surfaceCanvas2.width}x${surfaceCanvas2.height}` : 'N/A',
                            canvas3Dimensions: surfaceCanvas3 ? `${surfaceCanvas3.width}x${surfaceCanvas3.height}` : 'N/A',
                        },
                        LogTag.DYNAMIC_CANVAS
                    );

                    // Capture canvas images and use pre-calculated table data
                    if (surfaceCanvas1) {
                        surfaceDesignImages.canvas1 = captureCanvasAsJpeg(surfaceCanvas1, jpegQuality);
                        surfaceDesignImages.tableData1 = designTableData.surfaceDesignTableData.tableData1;
                    }
                    if (surfaceCanvas2) {
                        surfaceDesignImages.canvas2 = captureCanvasAsJpeg(surfaceCanvas2, jpegQuality);
                        surfaceDesignImages.tableData2 = designTableData.surfaceDesignTableData.tableData2;
                    }
                    if (surfaceCanvas3) {
                        surfaceDesignImages.canvas3 = captureCanvasAsJpeg(surfaceCanvas3, jpegQuality);
                        surfaceDesignImages.tableData3 = designTableData.surfaceDesignTableData.tableData3;
                    }

                    logDebug(
                        'PdfImageCaptureService',
                        'Airport surface design images and table data captured (data-driven)',
                        {
                            hasCanvas1: !!surfaceDesignImages.canvas1,
                            hasCanvas2: !!surfaceDesignImages.canvas2,
                            hasCanvas3: !!surfaceDesignImages.canvas3,
                            canvas1Length: surfaceDesignImages.canvas1?.length || 0,
                            canvas2Length: surfaceDesignImages.canvas2?.length || 0,
                            canvas3Length: surfaceDesignImages.canvas3?.length || 0,
                            hasTableData1: !!surfaceDesignImages.tableData1,
                            hasTableData2: !!surfaceDesignImages.tableData2,
                            hasTableData3: !!surfaceDesignImages.tableData3,
                        },
                        LogTag.DYNAMIC_CANVAS
                    );
                }

                // Capture base design canvas elements with pre-calculated table data
                if (projectData.baseDesign?.enableBaseDesign === 'yes' && designTableData.baseDesignTableData) {
                    logDebug('PdfImageCaptureService', 'Capturing Airport base design canvases with pre-calculated table data...', undefined, LogTag.DYNAMIC_CANVAS);

                    baseDesignImages = {};

                    // Find canvas elements by container ID
                    const baseCanvas1 = document.getElementById('base-design-unstabilized')?.querySelector('canvas');
                    const baseCanvas2 = document.getElementById('base-design-increased-passages')?.querySelector('canvas');
                    const baseCanvas3 = document.getElementById('base-design-stabilized')?.querySelector('canvas');

                    logDebug(
                        'PdfImageCaptureService',
                        'Airport base canvas elements found by container ID',
                        {
                            canvas1Found: !!baseCanvas1,
                            canvas2Found: !!baseCanvas2,
                            canvas3Found: !!baseCanvas3,
                        },
                        LogTag.DYNAMIC_CANVAS
                    );

                    // Capture canvas images and use pre-calculated table data
                    if (baseCanvas1) {
                        baseDesignImages.canvas1 = captureCanvasAsJpeg(baseCanvas1, jpegQuality);
                        baseDesignImages.tableData1 = designTableData.baseDesignTableData.tableData1;
                    }
                    if (baseCanvas2) {
                        baseDesignImages.canvas2 = captureCanvasAsJpeg(baseCanvas2, jpegQuality);
                        baseDesignImages.tableData2 = designTableData.baseDesignTableData.tableData2;
                    }
                    if (baseCanvas3) {
                        baseDesignImages.canvas3 = captureCanvasAsJpeg(baseCanvas3, jpegQuality);
                        baseDesignImages.tableData3 = designTableData.baseDesignTableData.tableData3;
                    }

                    logDebug('PdfImageCaptureService', 'Airport base design images and table data captured', undefined, LogTag.DYNAMIC_CANVAS);
                }

                // Capture subbase design canvas elements with pre-calculated table data
                if (projectData.subbaseDesign?.enableSubbaseDesign === 'yes' && designTableData.subbaseDesignTableData) {
                    logDebug('PdfImageCaptureService', 'Capturing Airport subbase design canvases with pre-calculated table data...', undefined, LogTag.DYNAMIC_CANVAS);

                    subbaseDesignImages = {};

                    // Find canvas elements by container ID
                    const subbaseCanvas1 = document.getElementById('subbase-design-unstabilized')?.querySelector('canvas');
                    const subbaseCanvas2 = document.getElementById('subbase-design-increased-passages')?.querySelector('canvas');
                    const subbaseCanvas3 = document.getElementById('subbase-design-stabilized')?.querySelector('canvas');

                    logDebug(
                        'PdfImageCaptureService',
                        'Airport subbase canvas elements found by container ID',
                        {
                            canvas1Found: !!subbaseCanvas1,
                            canvas2Found: !!subbaseCanvas2,
                            canvas3Found: !!subbaseCanvas3,
                        },
                        LogTag.DYNAMIC_CANVAS
                    );

                    // Capture canvas images and use pre-calculated table data
                    if (subbaseCanvas1) {
                        subbaseDesignImages.canvas1 = captureCanvasAsJpeg(subbaseCanvas1, jpegQuality);
                        subbaseDesignImages.tableData1 = designTableData.subbaseDesignTableData.tableData1;
                    }
                    if (subbaseCanvas2) {
                        subbaseDesignImages.canvas2 = captureCanvasAsJpeg(subbaseCanvas2, jpegQuality);
                        subbaseDesignImages.tableData2 = designTableData.subbaseDesignTableData.tableData2;
                    }
                    if (subbaseCanvas3) {
                        subbaseDesignImages.canvas3 = captureCanvasAsJpeg(subbaseCanvas3, jpegQuality);
                        subbaseDesignImages.tableData3 = designTableData.subbaseDesignTableData.tableData3;
                    }

                    logDebug('PdfImageCaptureService', 'Airport subbase design images and table data captured', undefined, LogTag.DYNAMIC_CANVAS);
                }
            } else if (projectData && !designTableData) {
                // Fallback to DOM scraping if table data is not provided (backward compatibility)
                logWarn('PdfImageCaptureService', 'Design table data not provided for Airport, falling back to DOM scraping approach', undefined, LogTag.DYNAMIC_CANVAS);
            }

            return {
                tireImageUnstabilized,
                tireImageIncreasedPassages,
                tireImageGeogrid,
                chartImg,
                surfaceDesignImages,
                baseDesignImages,
                subbaseDesignImages,
            };
        } catch (error) {
            throw error;
        }
    }

    /**
     * Cattura le immagini per Railway usando approccio deterministico
     * @param options opzioni di cattura
     * @returns Promise<IRailwayImages>
     */
    public async captureRailwayImages(options: IRailwayCaptureOptions = {}): Promise<IRailwayImages> {
        const { jpegQuality = 0.8 } = options;

        try {
            // Direct canvas queries for railway elements
            const canvasUnreinforced = this.findCanvasDeterministic(['tire-layer-unreinforced']);
            const canvasReinforced = this.findCanvasDeterministic(['tire-layer-reinforced']);

            if (!canvasUnreinforced || !canvasReinforced) {
                const missing = [];
                if (!canvasUnreinforced) missing.push('unreinforced');
                if (!canvasReinforced) missing.push('reinforced');
                throw new Error(`Railway canvas not found: missing ${missing.join(', ')} canvas`);
            }

            // Skip content validation - capture directly if canvases exist (simplified approach)

            const tireImageUnreinforced = captureCanvasAsJpeg(canvasUnreinforced, jpegQuality);
            const tireImageReinforced = captureCanvasAsJpeg(canvasReinforced, jpegQuality);

            // Debug: Log captured image sizes to verify content
            logDebug(
                'PdfImageCaptureService',
                'Railway captured image sizes:',
                {
                    tireImageUnreinforcedLength: tireImageUnreinforced?.length || 0,
                    tireImageReinforcedLength: tireImageReinforced?.length || 0,
                    hasValidImages: !!(tireImageUnreinforced && tireImageReinforced),
                },
                LogTag.PDF_IMAGES
            );

            return {
                tireImageUnreinforced,
                tireImageReinforced,
            };
        } catch (error) {
            throw error;
        }
    }

    /**
     * Create a parking deck capture method that uses container yard logic
     * @param options capture options
     * @returns Promise<IContainerYardImages>
     */
    public async captureParkingDeckImagesUnified(options: IContainerYardCaptureOptions = {}): Promise<IContainerYardImages> {
        return await this.captureContainerYardImages(options);
    }

    // =====================================
    // SESSIONSTORE METHODS
    // =====================================

    /**
     * Salva le immagini Container Yard in sessionStorage per l'accesso Combined PDF
     * @param images immagini da salvare
     * @param projectGuid GUID del progetto
     */
    public storeContainerYardImagesForCombinedPdf(images: IContainerYardImages, projectGuid: string, projectType: EProjectType = EProjectType.container_yard): void {
        // ✅ FIXED: Use centralized key generator to prevent mismatch
        const storageKey = PdfImageCaptureService.getStorageKey(projectType, projectGuid);

        // Use enhanced storage with cleanup
        this.storeImagesWithTimestamp(storageKey, images);
    }

    /**
     * Salva le immagini Unpaved in sessionStorage per l'accesso Combined PDF
     * @param images immagini da salvare
     * @param projectGuid GUID del progetto
     */
    public storeUnpavedImagesForCombinedPdf(images: IUnpavedImages, projectGuid: string): void {
        // ✅ Use centralized key generator
        const storageKey = PdfImageCaptureService.getStorageKey(EProjectType.unpaved, projectGuid);

        // Use enhanced storage with cleanup
        this.storeImagesWithTimestamp(storageKey, images);
    }

    /**
     * Public method to manually trigger cleanup of stale image data
     * Can be called from application lifecycle hooks or user actions
     */
    public cleanupStaleImages(): void {
        this.cleanupStaleImageData();
    }

    /**
     * Get storage usage statistics
     */
    public getStorageStats(): { totalKeys: number; imageKeys: number; estimatedSize: number } {
        let totalKeys = 0;
        let imageKeys = 0;
        let estimatedSize = 0;

        try {
            for (let i = 0; i < sessionStorage.length; i++) {
                const key = sessionStorage.key(i);
                if (key) {
                    totalKeys++;
                    const value = sessionStorage.getItem(key);
                    if (value) {
                        estimatedSize += key.length + value.length;

                        if (this.isImageStorageKey(key)) {
                            imageKeys++;
                        }
                    }
                }
            }
        } catch (error) {
            logError('PdfImageCaptureService', 'Error calculating storage stats:', error, LogTag.DYNAMIC_CANVAS);
        }

        return { totalKeys, imageKeys, estimatedSize };
    }

    /**
     * Salva le immagini Paved in sessionStorage per l'accesso Combined PDF
     * @param images immagini da salvare
     * @param projectGuid GUID del progetto
     */
    public storePavedImagesForCombinedPdf(images: IPavedImages, projectGuid: string): void {
        // ✅ Use centralized key generator
        const storageKey = PdfImageCaptureService.getStorageKey(EProjectType.paved, projectGuid);

        // Use enhanced storage with cleanup
        this.storeImagesWithTimestamp(storageKey, images);

        logDebug(
            'PdfImageCaptureService',
            'Paved images stored for Combined PDF access',
            {
                storageKey,
                hasSurfaceDesign: !!images.surfaceDesignImages,
                hasBaseDesign: !!images.baseDesignImages,
                hasSubbaseDesign: !!images.subbaseDesignImages,
                surfaceCanvasCount: images.surfaceDesignImages ? Object.keys(images.surfaceDesignImages).length : 0,
                baseCanvasCount: images.baseDesignImages ? Object.keys(images.baseDesignImages).length : 0,
                subbaseCanvasCount: images.subbaseDesignImages ? Object.keys(images.subbaseDesignImages).length : 0,
            },
            LogTag.DYNAMIC_CANVAS
        );
    }

    /**
     * ✅ CRITICAL FIX BUG-002: Store Railway images for combined PDF
     * Previously MISSING - Railway had no storage method
     * 
     * @param images Oggetto contenente le immagini Railway catturate
     * @param projectGuid GUID del progetto
     */
    public storeRailwayImagesForCombinedPdf(images: IRailwayImages, projectGuid: string): void {
        // ✅ Use centralized key generator
        const storageKey = PdfImageCaptureService.getStorageKey(EProjectType.railways, projectGuid);

        // Use enhanced storage with cleanup
        this.storeImagesWithTimestamp(storageKey, images);
    }

    /**
     * ✅ CRITICAL FIX BUG-002: Store Airport images for combined PDF
     * Previously MISSING - Airport had no storage method
     * 
     * @param images Oggetto contenente le immagini Airport catturate
     * @param projectGuid GUID del progetto
     */
    public storeAirportImagesForCombinedPdf(images: IAirportImages, projectGuid: string): void {
        // ✅ Use centralized key generator
        const storageKey = PdfImageCaptureService.getStorageKey(EProjectType.airport, projectGuid);

        // Use enhanced storage with cleanup
        this.storeImagesWithTimestamp(storageKey, images);
    }

    /**
     * Recupera le immagini Container Yard da sessionStorage
     * @param projectGuid GUID del progetto
     * @returns IContainerYardImages | null
     */
    public getStoredContainerYardImages(projectGuid: string, projectType: EProjectType = EProjectType.container_yard): IContainerYardImages | null {
        // ✅ FIXED: Use centralized key generator to prevent mismatch
        const storageKey = PdfImageCaptureService.getStorageKey(projectType, projectGuid);

        try {
            const stored = sessionStorage.getItem(storageKey);
            if (!stored) return null;

            const parsed = JSON.parse(stored);

            // Verifica che abbia tutti i campi necessari
            if (parsed.tireImg1 && parsed.tireImg2 && parsed.chartImg) {
                return {
                    tireImg1: parsed.tireImg1,
                    tireImg2: parsed.tireImg2,
                    chartImg: parsed.chartImg,
                };
            }
            return null;
        } catch (error) {
            return null;
        }
    }

    /**
     * Recupera le immagini Unpaved da sessionStorage
     * @param projectGuid GUID del progetto
     * @returns IUnpavedImages | null
     */
    public getStoredUnpavedImages(projectGuid: string): IUnpavedImages | null {
        const storageKey = `unpavedPdfImages_${projectGuid}`;

        try {
            const stored = sessionStorage.getItem(storageKey);
            if (!stored) return null;

            const parsed = JSON.parse(stored);

            // Verifica che abbia tutti i campi necessari
            if (parsed.tireImg1 && parsed.tireImg2) {
                return {
                    tireImg1: parsed.tireImg1,
                    tireImg2: parsed.tireImg2,
                    tireImg3: parsed.tireImg3,
                    chartImg: parsed.chartImg,
                };
            }
            return null;
        } catch (error) {
            return null;
        }
    }

    /**
     * Recupera le immagini Paved da sessionStorage
     * @param projectGuid GUID del progetto
     * @returns IPavedImages | null
     */
    public getStoredPavedImages(projectGuid: string): IPavedImages | null {
        const storageKey = `pavedPdfImages_${projectGuid}`;

        try {
            const stored = sessionStorage.getItem(storageKey);
            if (!stored) {
                logDebug('PdfImageCaptureService', 'No stored paved images found', { storageKey }, LogTag.DYNAMIC_CANVAS);
                return null;
            }

            const parsed = JSON.parse(stored);

            // Debug logging for dynamic sections
            logDebug(
                'PdfImageCaptureService',
                'Retrieved stored Paved images',
                {
                    storageKey,
                    hasSurfaceDesign: !!parsed.surfaceDesignImages,
                    hasBaseDesign: !!parsed.baseDesignImages,
                    hasSubbaseDesign: !!parsed.subbaseDesignImages,
                    surfaceCanvasCount: parsed.surfaceDesignImages ? Object.keys(parsed.surfaceDesignImages).length : 0,
                    baseCanvasCount: parsed.baseDesignImages ? Object.keys(parsed.baseDesignImages).length : 0,
                    subbaseCanvasCount: parsed.subbaseDesignImages ? Object.keys(parsed.subbaseDesignImages).length : 0,
                },
                LogTag.DYNAMIC_CANVAS
            );

            // Verifica che abbia tutti i campi necessari
            if (parsed.tireImageUnreinforced && parsed.tireImageReinforced && parsed.tireImageGeogrid) {
                const result: IPavedImages = {
                    tireImageUnreinforced: parsed.tireImageUnreinforced,
                    tireImageReinforced: parsed.tireImageReinforced,
                    tireImageGeogrid: parsed.tireImageGeogrid,
                    chartImg: parsed.chartImg,
                    // Include trafficData if present in the stored data
                    trafficData: parsed.trafficData || undefined,
                    // Include dynamic design sections if present
                    surfaceDesignImages: parsed.surfaceDesignImages || undefined,
                    baseDesignImages: parsed.baseDesignImages || undefined,
                    subbaseDesignImages: parsed.subbaseDesignImages || undefined,
                };
                return result;
            }
            return null;
        } catch (error) {
            logError('PdfImageCaptureService', 'Error retrieving stored Paved images:', error, LogTag.DYNAMIC_CANVAS);
            return null;
        }
    }

    /**
     * Auto-cleanup method to remove stale image data from sessionStorage
     * Called automatically when storing new data
     */
    private cleanupStaleImageData(): void {
        const maxAge = 24 * 60 * 60 * 1000; // 24 hours
        const currentTime = Date.now();

        try {
            const keysToRemove: string[] = [];

            // Check all sessionStorage keys for our pattern
            for (let i = 0; i < sessionStorage.length; i++) {
                const key = sessionStorage.key(i);
                if (key && this.isImageStorageKey(key)) {
                    const stored = sessionStorage.getItem(key);
                    if (stored) {
                        try {
                            const parsed = JSON.parse(stored);
                            const timestamp = parsed._timestamp || 0;

                            if (currentTime - timestamp > maxAge) {
                                keysToRemove.push(key);
                            }
                        } catch (parseError) {
                            // Invalid JSON, mark for removal
                            keysToRemove.push(key);
                        }
                    }
                }
            }

            // Remove stale keys
            keysToRemove.forEach(key => {
                sessionStorage.removeItem(key);
                logDebug('PdfImageCaptureService', 'Removed stale image data', { key }, LogTag.DYNAMIC_CANVAS);
            });

            if (keysToRemove.length > 0) {
                logDebug('PdfImageCaptureService', `Cleaned up ${keysToRemove.length} stale image entries`, {}, LogTag.DYNAMIC_CANVAS);
            }
        } catch (error) {
            logError('PdfImageCaptureService', 'Error during cleanup:', error, LogTag.DYNAMIC_CANVAS);
        }
    }

    /**
     * Check if a key matches our image storage pattern
     */
    private isImageStorageKey(key: string): boolean {
        return key.includes('PdfImages_') || key.includes('pdfImages_');
    }

    /**
     * Enhanced storage method with timestamp and cleanup
     */
    private storeImagesWithTimestamp<T>(storageKey: string, data: T): void {
        try {
            // Auto-cleanup before storing new data
            this.cleanupStaleImageData();

            // Add timestamp to data
            const dataWithTimestamp = {
                ...data,
                _timestamp: Date.now(),
                _version: '2.0',
            };

            sessionStorage.setItem(storageKey, JSON.stringify(dataWithTimestamp));
            logDebug(
                'PdfImageCaptureService',
                'Stored images with timestamp',
                {
                    storageKey,
                    timestamp: dataWithTimestamp._timestamp,
                },
                LogTag.DYNAMIC_CANVAS
            );
        } catch (error) {
            logError('PdfImageCaptureService', 'Failed to store images:', error, LogTag.DYNAMIC_CANVAS);

            // Fallback: try to clear some space and retry once
            try {
                this.cleanupStaleImageData();
                sessionStorage.setItem(
                    storageKey,
                    JSON.stringify({
                        ...data,
                        _timestamp: Date.now(),
                        _version: '2.0',
                    })
                );
            } catch (retryError) {
                logError('PdfImageCaptureService', 'Failed to store images after cleanup retry:', retryError, LogTag.DYNAMIC_CANVAS);
            }
        }
    }

    /**
     * Get stored Airport images from sessionStorage with error handling
     */
    public getStoredAirportImages(projectGuid: string): IAirportImages | null {
        const storageKey = `airportPdfImages_${projectGuid}`;

        try {
            const stored = sessionStorage.getItem(storageKey);
            if (!stored) {
                logDebug('PdfImageCaptureService', 'No stored airport images found', { storageKey }, LogTag.DYNAMIC_CANVAS);
                return null;
            }

            console.log('[getStoredAirportImages:dynamic_canvas] Found stored data for', storageKey);

            const parsed = JSON.parse(stored);

            // Debug logging for dynamic sections
            logDebug(
                'PdfImageCaptureService',
                'Retrieved stored Airport images',
                {
                    storageKey,
                    hasSurfaceDesign: !!parsed.surfaceDesignImages,
                    hasBaseDesign: !!parsed.baseDesignImages,
                    hasSubbaseDesign: !!parsed.subbaseDesignImages,
                    surfaceCanvasCount: parsed.surfaceDesignImages ? Object.keys(parsed.surfaceDesignImages).length : 0,
                    baseCanvasCount: parsed.baseDesignImages ? Object.keys(parsed.baseDesignImages).length : 0,
                    subbaseCanvasCount: parsed.subbaseDesignImages ? Object.keys(parsed.subbaseDesignImages).length : 0,
                },
                LogTag.DYNAMIC_CANVAS
            );

            // Verify that it has all necessary fields
            if (parsed.tireImageUnstabilized && parsed.tireImageIncreasedPassages && parsed.tireImageGeogrid) {
                const result: IAirportImages = {
                    tireImageUnstabilized: parsed.tireImageUnstabilized,
                    tireImageIncreasedPassages: parsed.tireImageIncreasedPassages,
                    tireImageGeogrid: parsed.tireImageGeogrid,
                    chartImg: parsed.chartImg,
                    // Include dynamic design sections if present
                    surfaceDesignImages: parsed.surfaceDesignImages || undefined,
                    baseDesignImages: parsed.baseDesignImages || undefined,
                    subbaseDesignImages: parsed.subbaseDesignImages || undefined,
                };
                return result;
            }
            return null;
        } catch (error) {
            logError('PdfImageCaptureService', 'Error retrieving stored Airport images:', error, LogTag.DYNAMIC_CANVAS);
            return null;
        }
    }

    /**
     * ✅ CRITICAL FIX BUG-003: Retrieve Railway images from sessionStorage
     * Previously MISSING - Railway had no retrieval method
     * 
     * @param projectGuid GUID del progetto
     * @returns Oggetto con immagini Railway o null se non trovate
     */
    public getStoredRailwayImages(projectGuid: string): IRailwayImages | null {
        // ✅ Use centralized key generator
        const storageKey = PdfImageCaptureService.getStorageKey(EProjectType.railways, projectGuid);

        try {
            const stored = sessionStorage.getItem(storageKey);
            if (!stored) {
                logDebug('PdfImageCaptureService', `No Railway images found for key: ${storageKey}`);
                return null;
            }

            const parsed = JSON.parse(stored);

            // Validate structure - Railway requires both images
            if (parsed.tireImageUnreinforced && parsed.tireImageReinforced) {
                return {
                    tireImageUnreinforced: parsed.tireImageUnreinforced,
                    tireImageReinforced: parsed.tireImageReinforced,
                };
            }

            logWarn('PdfImageCaptureService', 'Railway images incomplete', parsed);
            return null;
        } catch (error) {
            logError('PdfImageCaptureService', 'Error retrieving stored Railway images:', error);
            return null;
        }
    }

    /**
     * Capture Airport images deterministically
     * @param options opzioni di cattura
     * @returns Promise<IAirportImages>
     */
    private async captureAirportImagesDeterministic(options: IAirportCaptureOptions = {}): Promise<IAirportImages> {
        const { jpegQuality = 0.8, projectData, designTableData } = options;

        console.log('[AIRPORT_DEBUG] ======= STARTING AIRPORT CAPTURE =======');
        console.log('[AIRPORT_DEBUG] Options received:', { jpegQuality, hasProjectData: !!projectData, hasDesignTableData: !!designTableData });

        try {
            // Direct canvas queries for airport elements (classic)
            const canvasUnstabilized = this.findCanvasDeterministic(['tire-layer-unstabilized']);
            const canvasIncreasedPassages = this.findCanvasDeterministic(['tire-layer-increased-passages']);
            const canvasGeogrid = this.findCanvasDeterministic(['tire-layer-geogrid']);

            if (!canvasUnstabilized || !canvasIncreasedPassages || !canvasGeogrid) {
                const missing = [];
                if (!canvasUnstabilized) missing.push('unstabilized');
                if (!canvasIncreasedPassages) missing.push('increased-passages');
                if (!canvasGeogrid) missing.push('geogrid');
                throw new Error(`Airport canvas not found: missing ${missing.join(', ')} canvas`);
            }

            // Validate canvas content
            await this.validateCanvasContentDeterministic(canvasUnstabilized, 'unstabilized');
            await this.validateCanvasContentDeterministic(canvasIncreasedPassages, 'increased-passages');
            await this.validateCanvasContentDeterministic(canvasGeogrid, 'geogrid');

            const tireImageUnstabilized = captureCanvasAsJpeg(canvasUnstabilized, jpegQuality);
            const tireImageIncreasedPassages = captureCanvasAsJpeg(canvasIncreasedPassages, jpegQuality);
            const tireImageGeogrid = captureCanvasAsJpeg(canvasGeogrid, jpegQuality);

            // Airport projects don't typically use charts according to the optimization comments
            const chartImg = '';

            // Dynamic design sections capture - similar to Paved implementation
            let surfaceDesignImages: IAirportImages['surfaceDesignImages'];
            let baseDesignImages: IAirportImages['baseDesignImages'];
            let subbaseDesignImages: IAirportImages['subbaseDesignImages'];

            // Debug: Log all available canvas elements with design-related IDs
            const allCanvasElements = document.querySelectorAll('canvas');
            const designCanvasIds = Array.from(allCanvasElements)
                .map(canvas => canvas.id || canvas.parentElement?.id)
                .filter(id => id && id.includes('design'))
                .filter(Boolean);

            // Direct console.log to bypass logging filter
            console.log('[AIRPORT_DEBUG] Available design canvas IDs:', { designCanvasIds, totalCanvas: allCanvasElements.length });

            logDebug('PdfImageCaptureService', 'Available design canvas IDs found:', { designCanvasIds, totalCanvas: allCanvasElements.length }, LogTag.DYNAMIC_CANVAS);

            // Surface Design Section
            console.log('[AIRPORT_DEBUG] Looking for surface design canvas with IDs:', ['surface-design-unstabilized', 'surface-design-increased-passages', 'surface-design-stabilized']);

            logDebug(
                'PdfImageCaptureService',
                'Looking for surface design canvas...',
                {
                    targetIds: ['surface-design-unstabilized', 'surface-design-increased-passages', 'surface-design-stabilized'],
                },
                LogTag.DYNAMIC_CANVAS
            );

            const surfaceUnstabilizedCanvas = this.findCanvasDeterministic(['surface-design-unstabilized']);
            const surfaceIncreasedPassagesCanvas = this.findCanvasDeterministic(['surface-design-increased-passages']);
            const surfaceStabilizedCanvas = this.findCanvasDeterministic(['surface-design-stabilized']);

            console.log('[AIRPORT_DEBUG] Surface design canvas search results:', {
                unstabilized: !!surfaceUnstabilizedCanvas,
                increasedPassages: !!surfaceIncreasedPassagesCanvas,
                stabilized: !!surfaceStabilizedCanvas,
            });

            logDebug(
                'PdfImageCaptureService',
                'Surface design canvas search results:',
                {
                    unstabilized: !!surfaceUnstabilizedCanvas,
                    increasedPassages: !!surfaceIncreasedPassagesCanvas,
                    stabilized: !!surfaceStabilizedCanvas,
                },
                LogTag.DYNAMIC_CANVAS
            );

            if (surfaceUnstabilizedCanvas || surfaceIncreasedPassagesCanvas || surfaceStabilizedCanvas) {
                surfaceDesignImages = {};

                if (surfaceUnstabilizedCanvas) {
                    await this.validateCanvasContentDeterministic(surfaceUnstabilizedCanvas, 'surface-design-unstabilized');
                    surfaceDesignImages.canvas1 = captureCanvasAsJpeg(surfaceUnstabilizedCanvas, jpegQuality);
                }

                if (surfaceIncreasedPassagesCanvas) {
                    await this.validateCanvasContentDeterministic(surfaceIncreasedPassagesCanvas, 'surface-design-increased-passages');
                    surfaceDesignImages.canvas2 = captureCanvasAsJpeg(surfaceIncreasedPassagesCanvas, jpegQuality);
                }

                if (surfaceStabilizedCanvas) {
                    await this.validateCanvasContentDeterministic(surfaceStabilizedCanvas, 'surface-design-stabilized');
                    surfaceDesignImages.canvas3 = captureCanvasAsJpeg(surfaceStabilizedCanvas, jpegQuality);
                }

                // Extract surface design table data if available
                if (designTableData?.surfaceDesignTable) {
                    surfaceDesignImages.tableData1 = designTableData.surfaceDesignTable.tableData1;
                    surfaceDesignImages.tableData2 = designTableData.surfaceDesignTable.tableData2;
                    surfaceDesignImages.tableData3 = designTableData.surfaceDesignTable.tableData3;
                }

                logDebug(
                    'PdfImageCaptureService',
                    'Surface design images captured successfully',
                    {
                        canvasCount: Object.keys(surfaceDesignImages).filter(k => k.startsWith('canvas')).length,
                        hasTableData: !!designTableData?.surfaceDesignTable,
                    },
                    LogTag.DYNAMIC_CANVAS
                );
            }

            // Base Design Section
            logDebug(
                'PdfImageCaptureService',
                'Looking for base design canvas...',
                {
                    targetIds: ['base-design-unstabilized', 'base-design-increased-passages', 'base-design-stabilized'],
                },
                LogTag.DYNAMIC_CANVAS
            );

            const baseUnstabilizedCanvas = this.findCanvasDeterministic(['base-design-unstabilized']);
            const baseIncreasedPassagesCanvas = this.findCanvasDeterministic(['base-design-increased-passages']);
            const baseStabilizedCanvas = this.findCanvasDeterministic(['base-design-stabilized']);

            logDebug(
                'PdfImageCaptureService',
                'Base design canvas search results:',
                {
                    unstabilized: !!baseUnstabilizedCanvas,
                    increasedPassages: !!baseIncreasedPassagesCanvas,
                    stabilized: !!baseStabilizedCanvas,
                },
                LogTag.DYNAMIC_CANVAS
            );

            if (baseUnstabilizedCanvas || baseIncreasedPassagesCanvas || baseStabilizedCanvas) {
                baseDesignImages = {};

                if (baseUnstabilizedCanvas) {
                    await this.validateCanvasContentDeterministic(baseUnstabilizedCanvas, 'base-design-unstabilized');
                    baseDesignImages.canvas1 = captureCanvasAsJpeg(baseUnstabilizedCanvas, jpegQuality);
                }

                if (baseIncreasedPassagesCanvas) {
                    await this.validateCanvasContentDeterministic(baseIncreasedPassagesCanvas, 'base-design-increased-passages');
                    baseDesignImages.canvas2 = captureCanvasAsJpeg(baseIncreasedPassagesCanvas, jpegQuality);
                }

                if (baseStabilizedCanvas) {
                    await this.validateCanvasContentDeterministic(baseStabilizedCanvas, 'base-design-stabilized');
                    baseDesignImages.canvas3 = captureCanvasAsJpeg(baseStabilizedCanvas, jpegQuality);
                }

                // Extract base design table data if available
                if (designTableData?.baseDesignTable) {
                    baseDesignImages.tableData1 = designTableData.baseDesignTable.tableData1;
                    baseDesignImages.tableData2 = designTableData.baseDesignTable.tableData2;
                    baseDesignImages.tableData3 = designTableData.baseDesignTable.tableData3;
                }

                logDebug(
                    'PdfImageCaptureService',
                    'Base design images captured successfully',
                    {
                        canvasCount: Object.keys(baseDesignImages).filter(k => k.startsWith('canvas')).length,
                        hasTableData: !!designTableData?.baseDesignTable,
                    },
                    LogTag.DYNAMIC_CANVAS
                );
            }

            // Subbase Design Section
            logDebug(
                'PdfImageCaptureService',
                'Looking for subbase design canvas...',
                {
                    targetIds: ['subbase-design-unstabilized', 'subbase-design-increased-passages', 'subbase-design-stabilized'],
                },
                LogTag.DYNAMIC_CANVAS
            );

            const subbaseUnstabilizedCanvas = this.findCanvasDeterministic(['subbase-design-unstabilized']);
            const subbaseIncreasedPassagesCanvas = this.findCanvasDeterministic(['subbase-design-increased-passages']);
            const subbaseStabilizedCanvas = this.findCanvasDeterministic(['subbase-design-stabilized']);

            logDebug(
                'PdfImageCaptureService',
                'Subbase design canvas search results:',
                {
                    unstabilized: !!subbaseUnstabilizedCanvas,
                    increasedPassages: !!subbaseIncreasedPassagesCanvas,
                    stabilized: !!subbaseStabilizedCanvas,
                },
                LogTag.DYNAMIC_CANVAS
            );

            if (subbaseUnstabilizedCanvas || subbaseIncreasedPassagesCanvas || subbaseStabilizedCanvas) {
                subbaseDesignImages = {};

                if (subbaseUnstabilizedCanvas) {
                    await this.validateCanvasContentDeterministic(subbaseUnstabilizedCanvas, 'subbase-design-unstabilized');
                    subbaseDesignImages.canvas1 = captureCanvasAsJpeg(subbaseUnstabilizedCanvas, jpegQuality);
                }

                if (subbaseIncreasedPassagesCanvas) {
                    await this.validateCanvasContentDeterministic(subbaseIncreasedPassagesCanvas, 'subbase-design-increased-passages');
                    subbaseDesignImages.canvas2 = captureCanvasAsJpeg(subbaseIncreasedPassagesCanvas, jpegQuality);
                }

                if (subbaseStabilizedCanvas) {
                    await this.validateCanvasContentDeterministic(subbaseStabilizedCanvas, 'subbase-design-stabilized');
                    subbaseDesignImages.canvas3 = captureCanvasAsJpeg(subbaseStabilizedCanvas, jpegQuality);
                }

                // Extract subbase design table data if available
                if (designTableData?.subbaseDesignTable) {
                    subbaseDesignImages.tableData1 = designTableData.subbaseDesignTable.tableData1;
                    subbaseDesignImages.tableData2 = designTableData.subbaseDesignTable.tableData2;
                    subbaseDesignImages.tableData3 = designTableData.subbaseDesignTable.tableData3;
                }

                logDebug(
                    'PdfImageCaptureService',
                    'Subbase design images captured successfully',
                    {
                        canvasCount: Object.keys(subbaseDesignImages).filter(k => k.startsWith('canvas')).length,
                        hasTableData: !!designTableData?.subbaseDesignTable,
                    },
                    LogTag.DYNAMIC_CANVAS
                );
            }

            // Final summary logging
            logDebug(
                'PdfImageCaptureService',
                'Airport dynamic canvas capture summary',
                {
                    hasSurfaceDesign: !!surfaceDesignImages,
                    hasBaseDesign: !!baseDesignImages,
                    hasSubbaseDesign: !!subbaseDesignImages,
                    surfaceCanvasCount: surfaceDesignImages ? Object.keys(surfaceDesignImages).filter(k => k.startsWith('canvas')).length : 0,
                    baseCanvasCount: baseDesignImages ? Object.keys(baseDesignImages).filter(k => k.startsWith('canvas')).length : 0,
                    subbaseCanvasCount: subbaseDesignImages ? Object.keys(subbaseDesignImages).filter(k => k.startsWith('canvas')).length : 0,
                },
                LogTag.DYNAMIC_CANVAS
            );

            return {
                tireImageUnstabilized,
                tireImageIncreasedPassages,
                tireImageGeogrid,
                chartImg,
                // Dynamic design sections
                surfaceDesignImages,
                baseDesignImages,
                subbaseDesignImages,
            };
        } catch (error) {
            throw error;
        }
    }

    /**
     * Capture Railway images deterministically
     * @param options opzioni di cattura
     * @returns Promise<IRailwayImages>
     */
    private async captureRailwayImagesDeterministic(options: IRailwayCaptureOptions = {}): Promise<IRailwayImages> {
        const { jpegQuality = 0.8 } = options;

        try {
            // Direct canvas queries for railway elements
            const canvasUnreinforced = this.findCanvasDeterministic(['tire-layer-unreinforced']);
            const canvasReinforced = this.findCanvasDeterministic(['tire-layer-reinforced']);

            if (!canvasUnreinforced || !canvasReinforced) {
                const missing = [];
                if (!canvasUnreinforced) missing.push('unreinforced');
                if (!canvasReinforced) missing.push('reinforced');
                throw new Error(`Railway canvas not found: missing ${missing.join(', ')} canvas`);
            }

            // Skip content validation - capture directly if canvases exist (simplified approach)

            const tireImageUnreinforced = captureCanvasAsJpeg(canvasUnreinforced, jpegQuality);
            const tireImageReinforced = captureCanvasAsJpeg(canvasReinforced, jpegQuality);

            // Debug: Log captured image sizes to verify content
            logDebug(
                'PdfImageCaptureService',
                'Railway captured image sizes:',
                {
                    tireImageUnreinforcedLength: tireImageUnreinforced?.length || 0,
                    tireImageReinforcedLength: tireImageReinforced?.length || 0,
                    hasValidImages: !!(tireImageUnreinforced && tireImageReinforced),
                },
                LogTag.PDF_IMAGES
            );

            return {
                tireImageUnreinforced,
                tireImageReinforced,
            };
        } catch (error) {
            throw error;
        }
    }

    /**
     * ✅ CRITICAL FIX: Centralized sessionStorage key generator
     * Prevents Container Yard key mismatch bug (BUG-001)
     * 
     * @param projectType - EProjectType enum value
     * @param projectGuid - Project GUID string
     * @returns Standardized storage key in format: {projectType}PdfImages_{guid}
     * 
     * @example
     * getStorageKey(EProjectType.container_yard, '123') // 'container_yardPdfImages_123'
     * getStorageKey(EProjectType.paved, '456')          // 'pavedPdfImages_456'
     */
    public static getStorageKey(projectType: EProjectType, projectGuid: string): string {
        // Use projectType enum value directly (with underscore for container_yard)
        // This matches the pattern used in usePdfExport.ts and resolves key mismatch
        return `${projectType}PdfImages_${projectGuid}`;
    }
}

// Esporta sia la classe (per metodi statici) che l'istanza singleton
export { PdfImageCaptureService };
export const pdfImageCaptureService = new PdfImageCaptureService();
export default pdfImageCaptureService;
